import React, { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import { Particles } from '../components/Particles.tsx';
import { 
  Button, 
  Snackbar, 
  Dialog, 
  DialogActions, 
  DialogTitle, 
  DialogContent,
  Typography, 
  Box, 
  Zoom, 
  Stack,
  Alert,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import { useThemeContext } from '../contexts/ThemeContext.tsx';

// --- STYLED COMPONENTS JAVÍTÁSA (shouldForwardProp hozzáadása) ---

interface StyledProps {
  darkMode?: boolean;
}

const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<StyledProps>(({ theme, darkMode }) => ({
  '& .MuiPaper-root': {
    borderRadius: '24px',
    border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
    background: darkMode 
      ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' 
      : 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
    color: darkMode ? 'white' : '#1a1a2e',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 15px 35px rgba(0, 0, 0, 0.3)',
    padding: theme.spacing(1),
  },
}));

const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<StyledProps>(({ theme, darkMode }) => ({
  borderRadius: '12px',
  padding: '10px 24px',
  fontWeight: 'bold',
  textTransform: 'none',
  background: darkMode 
    ? 'linear-gradient(45deg, #00d2ff, #3a7bd5)' 
    : theme.palette.primary.main,
  color: 'white',
  '&:hover': {
    background: darkMode 
      ? 'linear-gradient(45deg, #3a7bd5, #00d2ff)' 
      : theme.palette.primary.dark,
  }
}));

// --- SEGÉDFÜGGVÉNYEK ---

const getRandomPosition = () => {
  const buttonSize = 100;
  // A fejléc (Navbar) kb 64px, ezért eltoljuk az y tengelyt
  const x = Math.random() * (window.innerWidth - buttonSize);
  const y = Math.random() * (window.innerHeight - buttonSize - 100) + 100;
  return { top: y, left: x };
};

const getRandomActionLimit = () => Math.floor(Math.random() * (25 - 5 + 1)) + 5;

const insults = [
  "I thought you could do better than that, Miauuu!",
  "Really? Is that all you've got?",
  "Too slow, human!",
  "Not even worth my nine lives!",
  "You move like a snail!",
  "Is this your best attempt? Hah!",
];

// --- GAME COMPONENT ---

const CatchMeGame: React.FC = () => {
  const { darkMode } = useThemeContext();
  const navigate = useNavigate();

  const [actionCount, setActionCount] = useState(0);
  const [maxActions, setMaxActions] = useState(getRandomActionLimit());
  const [catPosition, setCatPosition] = useState(getRandomPosition());
  const [catMessage, setCatMessage] = useState('');
  const [catVisible, setCatVisible] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [canCatch, setCanCatch] = useState(false);
  const [openRulesDialog, setOpenRulesDialog] = useState(true);

  useEffect(() => {
    if (actionCount >= maxActions) {
      setCanCatch(true);
      setCatMessage("Alright, you win, but only because I'm tired! Click me! 🐾");
    }
  }, [actionCount, maxActions]);

  const performAction = useCallback(() => {
    const actions = ['vanish', 'slide', 'run'];
    const randomAction = actions[Math.floor(Math.random() * actions.length)];

    if (randomAction === 'vanish') {
      setCatVisible(false);
      setTimeout(() => {
        setCatPosition(getRandomPosition());
        setCatVisible(true);
      }, 300);
    } else {
      setCatPosition(getRandomPosition());
    }

    const randomInsult = insults[Math.floor(Math.random() * insults.length)];
    setCatMessage(randomInsult);
  }, []);

  const handleMouseMove = useCallback((event: MouseEvent) => {
    // Ne mozogjon, ha a dialógusok nyitva vannak
    if (canCatch || openRulesDialog || openDialog) return;

    const distance = Math.sqrt(
      Math.pow(event.clientX - (catPosition.left + 40), 2) +
      Math.pow(event.clientY - (catPosition.top + 40), 2)
    );

    if (distance < 120) {
      setActionCount(prev => prev + 1);
      performAction();
    }
  }, [catPosition, canCatch, openRulesDialog, openDialog, performAction]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  const handleCatch = () => {
    if (canCatch) {
      setOpenSnackbar(true);
      setTimeout(() => setOpenDialog(true), 1200);
    }
  };

  const resetGame = () => {
    setActionCount(0);
    setMaxActions(getRandomActionLimit());
    setCatPosition(getRandomPosition());
    setCatMessage('');
    setCanCatch(false);
    setOpenDialog(false);
  };

  return (
    <Box sx={{ width: '100vw', height: '100vh', overflow: 'hidden', position: 'relative' }}>
      
      {/* Macska és buborék */}
      <Box
        sx={{
          position: 'absolute',
          top: catPosition.top,
          left: catPosition.left,
          transition: 'top 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), left 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          visibility: catVisible ? 'visible' : 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 10,
        }}
      >
        {catMessage && (
          <Paper
            elevation={4}
            sx={{
              p: 1.5,
              mb: 1,
              borderRadius: '15px 15px 15px 0',
              maxWidth: '200px',
              backgroundColor: darkMode ? 'rgba(255,255,255,0.95)' : 'white',
              color: 'black',
              fontSize: '0.9rem',
              fontWeight: 'bold',
            }}
          >
            {catMessage}
          </Paper>
        )}
        <Button
          onClick={handleCatch}
          disableRipple
          sx={{
            fontSize: '3.5rem',
            minWidth: 'auto',
            p: 0,
            lineHeight: 1,
            cursor: canCatch ? 'pointer' : 'default',
            '&:hover': { background: 'none' },
            animation: canCatch ? 'pulse 1.5s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.2)' },
              '100%': { transform: 'scale(1)' },
            }
          }}
        >
          {canCatch ? '😸' : '😼'}
        </Button>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ borderRadius: '12px', fontWeight: 'bold' }}>
          🎉 Achievement Unlocked: Cat Catcher!
        </Alert>
      </Snackbar>

      {/* Játékszabályok Dialog */}
      <AnimatedDialog 
        open={openRulesDialog} 
        onClose={() => setOpenRulesDialog(false)}
        TransitionComponent={Zoom}
        darkMode={darkMode}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.5rem', color: darkMode ? '#00d2ff' : 'primary.main' }}>
          🐱 Catch Me If You Can!
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            This feline friend is extremely fast and a bit rude.
          </Typography>
          <Box sx={{ p: 2, bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', borderRadius: '12px' }}>
            <Typography variant="body2" component="div" sx={{ lineHeight: 1.8 }}>
              • Move your mouse close to the cat to chase it.<br />
              • Each chase exhausts the cat a little.<br />
              • Once it's tired, its heart will open and you can catch it!<br />
              • Don't listen to its insults, it's just being a cat.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 3 }}>
          <Button onClick={() => navigate(-1)} sx={{ color: darkMode ? 'white' : 'text.primary' }}>Back</Button>
          <ActionButton darkMode={darkMode} onClick={() => setOpenRulesDialog(false)}>
            I'm Ready!
          </ActionButton>
        </DialogActions>
      </AnimatedDialog>

      {/* Play Again Dialog */}
      <AnimatedDialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        TransitionComponent={Zoom}
        darkMode={darkMode}
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          Victory! 🏆
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="h6">You've successfully outsmarted the cat!</Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
            Would you like to try another round?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', p: 3, gap: 2 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ color: darkMode ? 'white' : 'text.primary' }}>
            Rest now
          </Button>
          <ActionButton darkMode={darkMode} onClick={resetGame}>
            Play Again
          </ActionButton>
        </DialogActions>
      </AnimatedDialog>

    </Box>
  );
};

// --- MAIN WRAPPER ---

const CatchMe: React.FC = () => {
  const isAuthenticated = !!Cookies.get('token');

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }}>
      <Box sx={{
        position: 'absolute',
        top: 0, left: 0, width: '100%', height: '100%',
        zIndex: -1,
      }}>
        <Particles />
      </Box>

      {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}
      <CatchMeGame />
    </Box>
  );
};

export default CatchMe;